import type { ContactFieldDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { SelectField } from '../../ui/Field.js'
import { count } from '../../ui/format.js'
import { Popover } from '../../ui/Popover.js'
import { Declared } from '../contacts/ContactFields.js'

/**
 * Setting one column on every segment ticked.
 *
 * **Only the columns this organisation declared.** The four a segment comes
 * with are not things to share across a selection, and that is the argument
 * that kept bulk edit off this board until declared columns existed: fifty
 * segments under one name is a mistake, and fifty sharing one rule list makes
 * forty-nine of them redundant. "Asks for" and the two dates are not values
 * anybody sets at all. A declared column is the opposite — "owner", "tier",
 * "which campaign was this built for" — and setting it across a dozen at once
 * is the whole reason somebody declared it.
 *
 * So the control is absent, not empty, until a column has been declared. A
 * picker offering nothing is a question with no answers.
 *
 * **One column, not a form**, for the reason the contacts board's says: a form
 * setting three at once would make "leave this alone" and "set this to empty"
 * the same gesture.
 *
 * **Nothing here changes who a campaign reaches.** A segment's audience is its
 * rules and its members, and this writes neither — it carries each segment's own
 * name and definition back unchanged, because saving replaces a segment whole.
 *
 * The value is asked for through the same `Declared` control the segment's own
 * dialog and the contact form use. A control of its own here would be a second
 * answer to what a status column may hold.
 */
export function SegmentBulkEdit({
  fields,
  picked,
  busy,
  onApply,
}: {
  /** The columns declared on segments. Nothing else can be set this way. */
  readonly fields: readonly ContactFieldDto[]
  /** How many are ticked, so the button can say it rather than imply it. */
  readonly picked: number
  readonly busy: boolean
  readonly onApply: (key: string, value: string, label: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [chosen, setChosen] = useState('')
  const [value, setValue] = useState('')

  const field = fields.find((one) => one.key === chosen) ?? fields[0]
  if (field === undefined) return null

  /*
   * Changing which column is being set clears the value.
   *
   * A value carried across from another column is one this one may not offer —
   * a dropdown would silently show its first option while the state said
   * something else, which is the mistake the board's filter made until
   * `applyPatch` became the one place a field change is handled.
   */
  const pick = (key: string) => {
    setChosen(key)
    setValue('')
  }

  return (
    <Popover
      align="right"
      label="Change a column on the segments you have ticked"
      onClose={() => setOpen(false)}
      onToggle={() => setOpen((was) => !was)}
      open={open}
      panelClassName="w-[22rem] p-3"
      trigger="Edit a column"
      triggerClassName="rounded-pill border border-line px-4 py-2 font-semibold text-[13px] text-t2 hover:text-t1"
    >
      <SelectField
        disabled={busy}
        id="segment-bulk-column"
        label="Column"
        onChange={pick}
        options={fields.map((one) => [one.key, one.label] as const)}
        value={field.key}
      />

      <Declared
        busy={busy}
        field={field}
        id="segment-bulk-value"
        onChange={setValue}
        value={value}
      />

      {/* Says which column, how many, and whether it sets or clears. A bulk
          action whose button reads only "Apply" is one people press without
          reading, and there is no undo for the values it overwrites. */}
      <button
        className="mt-2 w-full rounded-pill bg-brand px-3 py-2 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
        disabled={busy}
        onClick={() => {
          setOpen(false)
          onApply(field.key, value, field.label)
        }}
        type="button"
      >
        {value === '' ? 'Clear' : 'Set'} {field.label} for {count(picked)}
      </button>
    </Popover>
  )
}
